import React from 'react';
import { Link } from 'react-router';
import { Meta } from 'common/components/meta';
import Support from 'marketing/containers/support';
import FaqList from 'common/components/faqs';
import PricingCategory from 'marketing/containers/pricing_category';
import { fetchCategoriesIfNeeded } from 'common/ducks/categories';
import { fetchReviewStatsIfNeeded } from 'marketing/ducks';
import Attribute from 'marketing/common/attribute';
import { getCanonicalURL } from 'common/utils';
import IntercomChat from 'common/containers/intercom_chat';
import partial from 'lodash/partial';
import {
  trackMixpanel, trackClickWithMixpanel, MP_VIEWED_MARKETING_PAGE_EVENT_NAME, MP_CLICKED_DESIGN_CONSULTATION_EVENT_NAME, MP_CLICKED_WORK_1_TO_1_EVENT_NAME, MP_CLICKED_PRICING_FAQ_ITEM_EVENT_NAME
} from 'common/tracking';

const QUESTIONS = [{
  question: 'How much does it cost to post a project?',
  answer: 'All our prices include an award, which goes directly to the winning creative and crowdspring\'s fee. When you post your project, you\'ll choose between 4 different packages: Silver, Gold, Elite and Platinum, each of which includes the award, our fee, and a set of features. You\'ll always know exactly what you\'ll pay. There are no hidden costs and no surprises.',
}, {
  question: 'What is the difference between Silver, Gold, Elite and Platinum packages?',
  answer: 'We are proud to offer flexible pricing that\'s perfect for businesses of any size. Silver projects are great for small companies that don\'t require privacy or collaboration (some of these are available as a la carte options in Silver projects). Gold projects are perfect for slightly more established companies that need additional privacy and control. Elite projects are perfect for companies of any size who want to work with a small group of our very best designers. Platinum projects are great for companies that need maximum privacy and control over their projects. When you post a Platinum project, you\'ll work solely with our best creatives. These projects include ALL features and are perfect for agencies, funded startups, and established companies.',
}, {
  question: 'What are the benefits of multiple awards in a project?',
  answer: 'Multiple awards significantly boost your project\'s participation because creatives have more chances to win an award. By purchasing multiple entries, you can also combine the different elements or ideas. You can add additional awards when you post your project or after your project posts. You can add as many additional awards as you want – our only rule after a project has started is that the awards must be at least equal to your lowest award.',
}, {
  question: 'Who owns the intellectual property when you pick the winning entry in your project?',
  answer: 'When you receive your final deliverables in the project and approve them, crowdspring pays the winning creative. At that time, you become the full owner of the intellectual property you purchased in your project.',
}, {
  question: 'Why does crowdspring offer a 100% money back guarantee?',
  answer: 'If you\'re not completely happy with your design or writing project, or if your naming project doesn\'t receive at least 100 entries, we\'ll promptly refund all your money. We\'re not happy until you\'re happy. Our guarantee shows that we stand behind our promise to make you happy.',
}, {
  question: 'Why choose crowdspring over an agency, freelancer or another design marketplace?',
  answer: 'With over 9 years of experience and over 60,000 business clients around the world who love us, crowdspring is the trusted leader when it comes to design and naming. Our flexible pricing is perfect for businesses of any size. If you want lots of choice and affordable, high-quality design and naming, you\'ve come to the right place.',
}];

class PricingPage extends React.Component {

  componentDidMount() {
    trackMixpanel(
      MP_VIEWED_MARKETING_PAGE_EVENT_NAME,
      {
        "pageName": "Pricing"
      }
    )
  }

  render() {
    return (
      <div className="l-content" id="content">
        <Meta
          title="Pricing of graphic design, logo design and naming projects"
          meta={[
            { name: 'robots', content: 'index,follow' },
            { name: 'description', content: "Find pricing for all graphic design, logo design, product design and business name projects on crowdspring. 100% money-back guarantee & custom copyright agreement included in every project." },
            { property: 'og:title', content: 'Pricing of graphic design, logo design and naming projects' },
            { property: 'og:url', content: getCanonicalURL('/pricing/') },
          ]}
          link={[{ rel: 'canonical', href: getCanonicalURL('/pricing/') }]} />

        <section className="block block-hero">
          <div className="row">
            <div className="small-10 medium-8 columns">
              <h1 className="hero-title">High-quality design at&nbsp;the&nbsp;right&nbsp;price.</h1>
              <h3 className="hero-subtitle">
                Our flexible pricing is perfect for businesses <br className="show-for-large" />of any size. Know&nbsp;up front
                exactly what you&#39;ll&nbsp;pay, with no&nbsp;hidden&nbsp;costs and no&nbsp;surprises.
              </h3>
            </div>
            <div className="small-6 medium-8 columns">
              <div className="artwork-container">
                <img
                  src="https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring_marketing_faq_artwork.png"
                  className="artwork"
                  alt="ARJUN689 Artwork" />
                <Attribute to="ARJUN689" />
              </div>
            </div>
          </div>
        </section>

        <section className="block thick block-packages">
          <PricingCategory />
        </section>

        <section className="block block-support thin invert-text">
          <div className="row">
            <div className="columns small-16 medium-10 text-center">
              <div className="block-header">
                <h2>Not sure what you need?</h2>
              </div>
              <p>Talk to one of our design experts for free.</p>
              <p>
                <Link className="btn btn-secondary" to="/help/contact/consultation/">
                  <span className="show-for-medium">Request</span> Free Design Consultation <i className="fa fa-long-arrow-right" />
                </Link>
              </p>
            </div>
            <div className="columns small-16 medium-6">
              <div className="avatar-stack">
                <img
                  className="avatar avatar-lg avatar-team avatar-round avatar-shadow"
                  src="https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring_marketing_support_marc.png" />
                <img
                  className="avatar avatar-lg avatar-team avatar-round avatar-shadow"
                  src="https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring_marketing_support_stefanie.jpg" />
                <img
                  className="avatar avatar-lg avatar-team avatar-round avatar-shadow"
                  src="https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring_marketing_support_ashlee.jpg" />
                <img
                  className="avatar avatar-lg avatar-team avatar-round avatar-shadow"
                  src="https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring_marketing_support_jordan.png" />
                <img
                  className="avatar avatar-lg avatar-team avatar-round avatar-shadow"
                  src="https://s3.amazonaws.com/crowdspring3-assets/marketing/crowdspring_marketing_support_katie.jpg" />
              </div>
            </div>
          </div>
        </section>

        <section className="block block-1to1">
          <div className="row columns">
            <div className="block-header">
              <h2>Not into crowds? <br />Work 1-to-1 with your favorite designer.</h2>
            </div>
          </div>
          <div className="row align-center">
            <div className="small-16 medium-8 large-6 columns">
              <div className="content-block">
                <h3>Find a designer</h3>
                <p>Thousands of professional designers work on crowdspring.
                Find a creative you love and negotiate a price to work with them directly. <br className="show-for-large" />1-to-1 Projects start at $200.
                </p>
              </div>
            </div>
            <div className="small-16 medium-8 large-6 columns">
              <div className="content-block">
                <h3>One platform, every creative need</h3>
                <p>
                  Our robust platform has all the tools you need to successfully manage your design project. <br className="hide-for-medium" />We handle the complexity so you can focus on getting great results.
                </p>
              </div>
            </div>
          </div>
          <div className="row columns text-center">
            <Link className="btn btn-primary" to="/start-a-project/start-1-1/">
              <span onClick={partial(trackClickWithMixpanel, MP_CLICKED_WORK_1_TO_1_EVENT_NAME, {pageName: "Pricing", pageArea: "Not into crowds?"}) }>
                Work 1-to-1
              </span>
            </Link>
          </div>
        </section>

        <section className="block block-faqs thick">
          <div className="row columns">
            <div className="block-header">
              <h2>Pricing FAQs</h2>
            </div>
          </div>
          <div className="row align-center">
            <div className="small-16 medium-10 columns">
              <FaqList data={QUESTIONS} eventTracking={{eventName: MP_CLICKED_PRICING_FAQ_ITEM_EVENT_NAME, eventData: {pageName: "Pricing", pageArea: "Pricing FAQs"}}} />
            </div>
          </div>
        </section>

        <Support
          consultationEventTracking={{
            eventName: MP_CLICKED_DESIGN_CONSULTATION_EVENT_NAME,
            eventData: {
              pageName: "Testimonials",
              version: "english",
              pageArea: "Support footer",
            }
          }}
        />

        <IntercomChat />
      </div>
    )
  }
};

PricingPage.need = [
  fetchCategoriesIfNeeded,
  fetchReviewStatsIfNeeded
];

export default PricingPage;



// WEBPACK FOOTER //
// ./marketing/pricing.jsx